import { createContext } from 'preact';
import type { OrganizerPdfSource } from '../lib/pdfForms';
import type { PdfPageGeometry } from '../../shared/pdf/geometry';
import type { PdfEngineIssue } from '../../shared/pdf/errors';
import { preparationService, type PdfPreparationService } from '../pdf/preparation/service';
import type { PreparationJob } from '../pdf/preparation/jobs';
import { reportClientError } from '../lib/clientErrors';

/**
 * Registration-level ownership of prepared PDFs. Each slot ('standard', 'document:<id>') holds at
 * most one artifact, identified by a key built from the values that slot's PDF actually uses.
 * A result whose key is no longer requested is dropped, so stale bytes can never become current.
 * Everything here is memory only and is released by clear() when the registration ends.
 */
export interface PreparedArtifact { slot: string; key: string; mode: 'preview' | 'final'; bytes: Uint8Array; pages: PdfPageGeometry[] }
export type SlotState =
  | { key: string; status: 'preparing' }
  | { key: string; status: 'ready'; artifact: PreparedArtifact }
  | { key: string; status: 'failed'; issues: PdfEngineIssue[] };

export class RegistrationArtifacts {
  private slots = new Map<string, SlotState>();
  private controllers = new Map<string, AbortController>();
  private sources = new Map<string, Promise<OrganizerPdfSource>>();
  private urls = new Map<PreparedArtifact, string>();
  private listeners = new Set<() => void>();
  constructor(private readonly service: PdfPreparationService = preparationService()) {}

  get(slot: string): SlotState | undefined { return this.slots.get(slot); }
  subscribe(listener: () => void): () => void { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }

  /** Prepare `key` for `slot` unless it is already current. Earlier queued work for the slot never starts. */
  request(slot: string, key: string, mode: PreparedArtifact['mode'], job: (signal: AbortSignal) => Promise<PreparationJob>): void {
    const current = this.slots.get(slot);
    if (current?.key === key) return;
    this.controllers.get(slot)?.abort();
    const controller = new AbortController(); this.controllers.set(slot, controller);
    this.set(slot, { key, status: 'preparing' });
    void (async () => {
      let next: SlotState;
      try {
        const result = await this.service.prepare(await job(controller.signal), controller.signal);
        next = result.ok ? { key, status: 'ready', artifact: { slot, key, mode, bytes: result.bytes, pages: result.pages } } : { key, status: 'failed', issues: result.issues };
      } catch (error) {
        if (controller.signal.aborted) return;
        if (!(error instanceof SourceUnavailable)) reportClientError('register: prepare form', error, { slot, mode });
        next = { key, status: 'failed', issues: error instanceof SourceUnavailable ? [error.issue] : [{ code: 'preparation_failed', message: 'This form could not be prepared. Check your connection and retry.' }] };
      }
      if (this.slots.get(slot)?.key === key && !controller.signal.aborted) this.set(slot, next);
    })();
  }
  /** Forget a failed or current result so the next request prepares it again. */
  retry(slot: string, sourceKey?: string): void {
    if (sourceKey) this.sources.delete(sourceKey);
    this.controllers.get(slot)?.abort(); this.controllers.delete(slot);
    this.set(slot, undefined);
  }
  /** Blank sources are fetched once per registration. A failure stays visible until retry. */
  source(key: string, load: () => Promise<OrganizerPdfSource>): Promise<OrganizerPdfSource> {
    let source = this.sources.get(key);
    if (!source) { source = load(); this.sources.set(key, source); source.catch(() => {}); }
    return source;
  }
  /** One object URL per artifact, revoked when the artifact is replaced or the registration ends. */
  url(artifact: PreparedArtifact): string {
    let url = this.urls.get(artifact);
    if (!url) { url = URL.createObjectURL(new Blob([artifact.bytes as BlobPart], { type: 'application/pdf' })); this.urls.set(artifact, url); }
    return url;
  }
  clear(): void {
    for (const controller of this.controllers.values()) controller.abort();
    this.controllers.clear(); this.sources.clear();
    for (const slot of [...this.slots.keys()]) this.set(slot, undefined);
  }

  private set(slot: string, state: SlotState | undefined): void {
    const previous = this.slots.get(slot);
    if (previous?.status === 'ready' && (state?.status !== 'ready' || state.artifact !== previous.artifact)) {
      const url = this.urls.get(previous.artifact);
      if (url) { URL.revokeObjectURL(url); this.urls.delete(previous.artifact); }
    }
    if (state) this.slots.set(slot, state); else this.slots.delete(slot);
    for (const listener of [...this.listeners]) listener();
  }
}
export class SourceUnavailable extends Error { constructor(readonly issue: PdfEngineIssue) { super(issue.code); } }

/** Organizer previews and tests outside a registration share this store. */
export const ArtifactsContext = createContext<RegistrationArtifacts | null>(null);
let fallback: RegistrationArtifacts | undefined;
export const defaultArtifacts = () => fallback ??= new RegistrationArtifacts();

/** Stable key text. Byte arrays contribute their length and two independent FNV-1a hashes. */
export function artifactKey(value: unknown): string {
  return JSON.stringify(value, (_key, v) => v instanceof Uint8Array ? `bytes:${v.length}:${fnv(v, 0x811c9dc5)}:${fnv(v, 0x050c5d1f)}` : v);
}
const summaries = new WeakMap<Uint8Array, string>();
/** Cached summary for large immutable inputs such as a blank template. */
export function bytesKey(bytes: Uint8Array): string {
  let key = summaries.get(bytes);
  if (!key) { key = artifactKey(bytes); summaries.set(bytes, key); }
  return key;
}
function fnv(bytes: Uint8Array, seed: number): string {
  let hash = seed;
  for (let i = 0; i < bytes.length; i++) { hash ^= bytes[i]; hash = Math.imul(hash, 0x01000193); }
  return (hash >>> 0).toString(16);
}
